import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, FileText, Mail, MessageSquare } from 'lucide-react';
import { Customer } from '../customer/customerType';
import { PaymentStatementToCustomerRequest, ProjectWithInvoices, InvoiceWithPayments } from './customerPaymentType';
import CustomerPaymentApi from './customerPaymentApi';
import { formatAddress, formatDate} from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { getAccountIdFromSessionStorage } from '../lib/global_function';

interface PaymentNotificationRequest {
  invoice_id: string;
  account_id: string;
  project_id: string;
  customer_id: string;
  send_method: 'email' | 'sms';
  recipient_email: string;
  recipient_phone: string;
  message_for: string;
}

const PaymentNotificationPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer Payment', path: '/main/customerpayment' },
    { label: 'Send Payment Notification', path: '/main/customerpayment/sendstatement' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const { project, invoice, customer }: { project?: ProjectWithInvoices; invoice?: InvoiceWithPayments; customer?: Customer } = location.state || {};

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSentSuccessfully, setIsSentSuccessfully] = useState(false);

  useEffect(() => {
    if (!project || !invoice || !customer) {
      setErrorMessage('Required data is missing. Please navigate from the customer payment page.');
    }
  }, [project, invoice, customer]);

const handleView = async () => {
  if (!invoice) {
    setErrorMessage('No invoice selected');
    return;
  }

  if (!customer) {
    setErrorMessage('No customer selected');
    return;
  }

  setErrorMessage(null);
  setIsLoading(true);

  try {
    const pdfBlob = await CustomerPaymentApi.viewPaymentStatementPdf(
      invoice.invoice_id,
      customer.customer_id
    );
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    
    // Optional: Clean up the object URL after opening
    setTimeout(() => URL.revokeObjectURL(url), 100);
    } 
  catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
  finally {
    setIsLoading(false);
  }
};




const handleSend = async () => {
  setErrorMessage(null);
  setSuccessMessage(null);
  setIsLoading(true);
  setIsSentSuccessfully(false);

  if (isLoading) {
    return;
  }

  if (!project || !invoice || !customer) {
    setErrorMessage('No required data selected');
    setIsLoading(false);
    return;
  }

  if (!sendMethod) {
    setErrorMessage('Please select a send method');
    setIsLoading(false);
    return;
  }

  const accountId = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
  if (!accountId) {
    setErrorMessage('Account not available.');
    setIsLoading(false);
    return;
  }

  try {
    const requestBody: PaymentStatementToCustomerRequest = {
      invoice_id: invoice.invoice_id,
      account_id: accountId,
      project_id: project.project_id,
      customer_id: customer.customer_id,
      send_method: sendMethod === 'text' ? 'sms' : sendMethod,
      recipient_email: customer.email || '',
      recipient_phone: customer.phone || '',
      message_for: 'payment_statement',
    };

    const response = await CustomerPaymentApi.sendPaymentStatementToCustomer(requestBody);
    setIsSentSuccessfully(true);
    setSuccessMessage('Your payment statement has been generated and is now being sent');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (error) {
    setErrorMessage(error instanceof Error ? error.message : 'Failed to send payment statement');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    setIsLoading(false);
  }
};
  const handleNavigateToCustomerPayment = () => {
    navigate('/main/customerpayment');
  };

  const formatCustomerAddress = () =>
    customer ? formatAddress(customer.address_1, customer.address_2, customer.city, customer.state, customer.zip) : '';

  const balanceDue = (invoice?.final_total || 0) - (invoice?.amount_paid || 0);
  const isBalanceZero = balanceDue <= 0;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Send Payment Notification</h1>
              {/* Error Messages */}
              {errorMessage && project && invoice && customer && (
                <Alert variant="error">
                  {errorMessage}
                </Alert>
              )}
              {successMessage && (
                <Alert variant="success">
                  {successMessage}
                </Alert>
              )}


          {/* Error when no data */}
          {errorMessage && (!project || !invoice || !customer) && (
            <Alert variant="error" className="mb-6">
              <div>
                <p>{errorMessage}</p>
                <div className="mt-4">
                  <Button size="medium" variant="default" onClick={handleNavigateToCustomerPayment}>
                    Go To Customer Payment
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          {project && invoice && customer ? (
            <div className="space-y-6">
              {/* Customer Information Section */}
              <div className="bg-white border border-gray-300 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground mb-4">
                  Customer Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold">Name:</span>{' '}
                    <span className="text-gray-700">{`${customer.first_name} ${customer.last_name}`}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Address:</span>{' '}
                    <span className="text-gray-700">{formatCustomerAddress()}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Email:</span>{' '}
                    <span className="text-gray-700">{customer.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Phone:</span>{' '}
                    <span className="text-gray-700">{customer.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Notification Details Section */}
              <div className="bg-white border border-gray-300 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                  <h3 className="text-base font-semibold text-foreground mb-3 sm:mb-0">
                    Payment Notification Details
                  </h3>
                  <Button
                    size="medium"
                    variant="default"
                    onClick={handleView}
                    disabled={isLoading}
                  >
                    <FileText size={16} /> View in PDF
                  </Button>
                </div>

                <div className="space-y-3 text-sm">
                  {/* Project Info */}
                  <div>
                    <span className="font-semibold">Project Name:</span>{' '}
                    <span className="text-gray-700">{project.name || `Project #${project.project_id}`}</span>
                    {', '}
                    <span className="font-semibold">Description:</span>{' '}
                    <span className="text-gray-700">{project.project_description || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Type:</span>{' '}
                    <span className="text-gray-700">{project.project_type || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Status:</span>{' '}
                    <span className={`font-bold ${project.is_active ? 'text-green-800' : 'text-red-800'}`}>
                      {project.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>

{/** 
                  <div>
                    <span className="font-semibold">Number of Invoices:</span>{' '}
                    <span className="text-gray-700">1</span>
                  </div>
*/}

                  {/* Invoice Info */}
                  <div>
                    <span className="font-semibold">Invoice #{invoice.invoice_id}:</span>{' '}
                    <span className="text-gray-700">{invoice.name || `Invoice #${invoice.invoice_id}`}</span>
                    {', '}
                    <span className="font-semibold">Issue Date:</span>{' '}
                    <span className="text-gray-700">{formatDate(invoice.issue_date) || 'N/A'}</span>
                    {', '}
                    <span className="font-semibold">Due Date:</span>{' '}
                    <span className="text-gray-700">{formatDate(invoice.due_date) || 'N/A'}</span>
                  </div>

                  {/* Financial Details */}
<div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2">
                      <span className="font-semibold text-sm text-gray-700">Financial Summary</span>
                    </div>
                    
                    {/* Calculation Section */}
                    <div className="divide-y divide-gray-200">
                      <div className="flex justify-between px-4 py-2 hover:bg-gray-50">
                        <span className="text-sm text-gray-600">Subtotal (Before Tax & Discount)</span>
                        <span className="text-sm font-semibold text-gray-900">${Number(invoice.sub_total_sum_lineitems || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100">
                        <span className="text-sm text-gray-600">Tax ({Number(invoice.tax_rate || 0).toFixed(2)}%)</span>
                        <span className="text-sm font-semibold text-gray-900">${Number(invoice.tax_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 hover:bg-gray-50">
                        <span className="text-sm text-gray-600">Discount</span>
                        <span className="text-sm font-semibold text-green-600">-${Number(invoice.discount_usd || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Total Section - Highlighted */}
                    <div className="border-t-2 border-gray-300 bg-blue-50 px-4 py-2.5">
                      <div className="flex justify-between">
                        <span className="text-base font-bold text-gray-900">Invoice Total</span>
                        <span className="text-base font-bold text-gray-900">${Number(invoice.final_total || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Payment Section */}
                    <div className="divide-y divide-gray-200">
                      <div className="flex justify-between px-4 py-2 hover:bg-gray-50">
                        <span className="text-sm text-gray-600">Paid</span>
                        <span className="text-sm font-semibold text-green-600">${Number(invoice.amount_paid || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100">
                        <span className="text-sm text-gray-600">Balance Due</span>
                        <span className={`text-sm font-bold ${isBalanceZero ? 'text-green-600' : 'text-red-600'}`}>
                          ${balanceDue.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Status Section - Highlighted */}
                    <div className={`border-t-2 px-4 py-2.5 ${
                      isBalanceZero 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-gray-900">Payment Status</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          isBalanceZero 
                            ? 'bg-green-200 text-green-900' 
                            : 'bg-red-200 text-red-900'
                        }`}>
                          {isBalanceZero ? 'PAID IN FULL' : 'OUTSTANDING'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Send Options Section */}
              {!isSentSuccessfully && (
                <div className="bg-white border border-gray-300 rounded-lg p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4">
                    Send Payment Notification to Customer by Email or SMS Text Message
                  </h3>
                  
                  {sendMethod && (
                    <div >
                      <Alert variant="info">
                      {sendMethod === 'email'
                        ? `The payment notification will be sent as a PDF file attachment in an email from MrJoe@MrJoe.pro. By clicking the Send Email, you confirm that the customer ${customer.first_name} ${customer.last_name} has agreed to receive this email to review the payment notification you provided.`
                        : `The payment notification will be sent as a link in a text message from (800)579-5808. By clicking the Send Text, you confirm that the customer ${customer.first_name} ${customer.last_name} has agreed to receive this message to review the payment notification you provided.`}
                    </Alert>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Radio Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="email"
                          checked={sendMethod === 'email'}
                          onChange={() => setSendMethod('email')}
                          className="w-4 h-4 text-primary"
                        />
                        <Mail size={16} />
                        <span className="text-sm font-medium">Email</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value="text"
                          checked={sendMethod === 'text'}
                          onChange={() => setSendMethod('text')}
                          className="w-4 h-4 text-primary"
                        />
                        <MessageSquare size={16} />
                        <span className="text-sm font-medium">Text</span>
                      </label>
                    </div>

                    {/* Send Button */}
                    <div>
                      <Button
                        size="medium"
                        variant="default"
                        onClick={handleSend}
                        disabled={isLoading || !sendMethod}
                      >
                        <Send size={20} />
                        {isLoading ? 'Sending...' : `Send${sendMethod ? ` ${sendMethod === 'email' ? 'Email' : 'Text'}` : ''}`}
                      </Button>
                    </div>
                  </div>
                </div>
              )}


            </div>
          ) : (
            !errorMessage && (
              <div className="text-center py-12 text-gray-500">
                <p>No payment data available to display.</p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentNotificationPage;